
import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import CanvasJSReact from '@canvasjs/react-charts';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const dataset = [
  { Available: 59, Booked: 57, Reserved: 86, Confirmed: 21, month: 'Jan' },
  { Available: 50, Booked: 52, Reserved: 78, Confirmed: 28, month: 'Feb' },
  { Available: 47, Booked: 53, Reserved: 106, Confirmed: 41, month: 'Mar' },
  { Available: 54, Booked: 56, Reserved: 92, Confirmed: 73, month: 'Apr' },
  { Available: 57, Booked: 69, Reserved: 92, Confirmed: 99, month: 'May' },
  { Available: 60, Booked: 63, Reserved: 103, Confirmed: 144, month: 'June' },
  { Available: 59, Booked: 60, Reserved: 105, Confirmed: 319, month: 'July' },
  { Available: 65, Booked: 60, Reserved: 106, Confirmed: 249, month: 'Aug' },
  { Available: 51, Booked: 51, Reserved: 95, Confirmed: 131, month: 'Sept' },
  { Available: 60, Booked: 65, Reserved: 97, Confirmed: 55, month: 'Oct' },
  { Available: 67, Booked: 64, Reserved: 76, Confirmed: 48, month: 'Nov' },
  { Available: 61, Booked: 70, Reserved: 103, Confirmed: 25, month: 'Dec' },
];

const mobileAndDesktopOS = [
  { label: 'Available', value: 40, color: '#00b300' },
  { label: 'Booked', value: 17, color: '#635bff' },
  { label: 'Reserved', value: 13, color: '#47a1ba' },
  { label: 'Confirmed', value: 30, color: '#c971bb' },
];

const chartSetting = {
  width: 700,
  height: 450,
  sx: {
    [`.${axisClasses.left} .${axisClasses.label}`]: {
      transform: 'translate(-20px, 0)',
    },
  },
};

const valueFormatter = (item) => `${item.value}`;

export default function IntegratedCharts() {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [renderTime, setRenderTime] = useState(0);

  useEffect(() => {
    const startTime = new Date();
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    const data = {
      labels: dataset.map((d) => d.month),
      datasets: [
        {
          label: 'Available',
          data: dataset.map((d) => d.Available),
          fill: false,
          borderColor: '#00b300',
          tension: 0.4,
        },
        {
          label: 'Booked',
          data: dataset.map((d) => d.Booked),
          fill: false,
          borderColor: '#635bff',
          tension: 0.4,
        },
        {
          label: 'Reserved',
          data: dataset.map((d) => d.Reserved),
          fill: false,
          borderColor: '#47a1ba',
          tension: 0.4,
        },
        {
          label: 'Confirmed',
          data: dataset.map((d) => d.Confirmed),
          fill: false,
          borderColor: '#c971bb',
          tension: 0.4,
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);

    const endTime = new Date();
    setRenderTime(endTime - startTime);
  }, []);

  const canvasJSOptions = {
    zoomEnabled: true,
    animationEnabled: true,
   
    data: [
      {
        type: "line",
        name: "Available",
        color: "#00b300",
        dataPoints: dataset.map((item, index) => ({
          x: index,
          y: item.Available,
          label: item.month
        }))
      },
      {
        type: "line",
        name: "Booked",
        color: "#635bff",
        dataPoints: dataset.map((item, index) => ({
          x: index,
          y: item.Booked,
          label: item.month
        }))
      },
      {
        type: "line",
        name: "Reserved",
        color: "#47a1ba",
        dataPoints: dataset.map((item, index) => ({
          x: index,
          y: item.Reserved,
          label: item.month
        }))
      },
      {
        type: "line",
        name: "Confirmed",
        color: "#c971bb",
        dataPoints: dataset.map((item, index) => ({
          x: index,
          y: item.Confirmed,
          label: item.month
        }))
      }
    ]
  };

  const spanStyle = {
    position: 'absolute',
    top: '10px',
    fontSize: '20px',
    fontWeight: 'bold',
    backgroundColor: '#d85757',
    padding: '0px 4px',
    color: '#ffffff'
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start', position: 'relative' }}>
        {/* Line Chart Section with PrimeReact */}
        <Box className="card" sx={{ flex: 1 }}>
          <Chart type="line" data={chartData} options={chartOptions} />
        </Box>

        {/* Bar and Pie Charts with MUI X Charts */}
        <Box sx={{ flex: 1 }}>
          <BarChart
            dataset={dataset}
            xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
            series={[
              { dataKey: 'Available', label: 'Available', color: '#00b300', valueFormatter },
              { dataKey: 'Booked', label: 'Booked', color: '#635bff', valueFormatter },
              { dataKey: 'Reserved', label: 'Reserved', color: '#47a1ba', valueFormatter },
              { dataKey: 'Confirmed', label: 'Confirmed', color: '#c971bb', valueFormatter },
            ]}
            {...chartSetting}
          />

      

          {/* CanvasJS Chart */}
          <Box sx={{ mt: 2 }}>
            <CanvasJSChart options={canvasJSOptions} />
           
          </Box>
        </Box>
      </Box>
    </>
  );
}